import {
  EntityDetailSkeleton,
  PageHeaderLeading,
  PageHeaderStack,
  Button,
  PageContainer,
  PageHeader,
  useArtifactCanvas,
  useAuth,
  EMPLOYER_HOME_PATH,
  managedEmployerOrganisationSwitcherEnabled,
  PageLineTabs,
  TabCountBadge,
  publishedJobPublicUrl,
  navigateWithThreadPreservation,
  getHrefWithThreadPreservation,
  lifecycleInviteAvailable,
  isActiveApplicationStatus,
  usePagePaneLayoutMode,
  isEmployerJobsListPathname,
  listReturnBackState,
  readListReturnPath,
  cn,
} from '@yeshuman/sdk'
import type { PageLineTabItem, PublicationStatus } from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { OpportunityDetailsCanvas } from '../opportunity-details-canvas'
import { useOpportunity, type OrganisationOpportunity } from '../../hooks/use-organisation-opportunities'
import { useOpportunityApplicants } from '../../hooks/use-opportunity-applicants'
import { prefetchJobProspectMatches } from '../../hooks/use-job-prospect-matches'
import { useQueryClient } from '@tanstack/react-query'
import { FileText, Kanban, UserPlus, Users } from 'lucide-react'
import { JobWorkspaceHeaderActions } from './job-workspace-header-actions'
import { LinkedInShareJobDialog } from './linkedin-share-job-dialog'
import { JobWorkspaceApplicantsTab } from './job-workspace-applicants-tab'
import { JobWorkspaceSetupTab } from './job-workspace-setup-tab'
import { JobWorkspaceTalentTab } from './job-workspace-talent-tab'
import { JobWorkspaceSuggestionsTab, useInviteTabSuggestionsCount } from './job-workspace-suggestions-tab'

type JobWorkspaceTab = 'details' | 'applicants' | 'talent' | 'suggestions'

const PUBLISH_FIRST_TAB_TITLE = 'Publish this job first.'

function inviteUnavailableTabTitle(status: PublicationStatus | undefined): string {
  if (status === 'archived') return 'Restore this job to send invites.'
  if (status === 'closed') return 'Re-open this job to send invites.'
  return PUBLISH_FIRST_TAB_TITLE
}

function activeTabFromPath(pathname: string): JobWorkspaceTab {
  if (pathname.includes('/suggestions')) return 'suggestions'
  if (pathname.includes('/talent')) return 'talent'
  if (pathname.includes('/applicants')) return 'applicants'
  return 'details'
}

const jobWorkspacePagePadding =
  'px-4 pt-4 pb-0 sm:px-6 sm:pt-6 sm:pb-0 @3card:px-[60px] @3card:pt-6 @3card:pb-0'

export function JobWorkspaceDefaultRedirect() {
  const { jobId = '' } = useParams<{ jobId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { data: opportunity, isLoading, error } = useOpportunity(jobId)

  useEffect(() => {
    if (!jobId) return
    if (isLoading) return
    if (error || !opportunity) {
      const fallback = managedEmployerOrganisationSwitcherEnabled() ? EMPLOYER_HOME_PATH : '/employer/jobs'
      const target = getHrefWithThreadPreservation(fallback)
      navigate(target, { replace: true, state: location.state })
      return
    }
    const tab =
      opportunity.publication_status === 'draft' || opportunity.publication_status === 'archived'
        ? 'details'
        : 'applicants'
    navigate(getHrefWithThreadPreservation(`/employer/jobs/${jobId}/${tab}`), {
      replace: true,
      state: location.state,
    })
  }, [jobId, isLoading, error, opportunity, navigate, location.state])

  return <EntityDetailSkeleton variant="job" ariaLabel="Loading job" />
}

export function JobWorkspaceLayout() {
  const { jobId = '', applicationId } = useParams<{ jobId: string; applicationId?: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { token } = useAuth()
  const { data: opportunity, isLoading, error } = useOpportunity(jobId)
  const { board } = useOpportunityApplicants(jobId || undefined)
  const [linkedInShareOpen, setLinkedInShareOpen] = useState(false)
  const jobDetailsOpenKeyRef = useRef(0)

  const inviteSuggestionsCount = useInviteTabSuggestionsCount(
    jobId || undefined,
    lifecycleInviteAvailable(opportunity?.publication_status),
  )

  const activeTab = activeTabFromPath(location.pathname)
  const pagePaneFill = usePagePaneLayoutMode() === 'fill'

  const pipelineCount = useMemo(
    () =>
      (board?.applicants ?? []).filter((applicant) => isActiveApplicationStatus(applicant.status))
        .length,
    [board?.applicants],
  )

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const jobsListBackTo =
    readListReturnPath(location.state, isEmployerJobsListPathname) ?? '/employer/jobs'
  const jobsListBack = {
    label: 'Back to job openings',
    to: jobsListBackTo,
    state: listReturnBackState(location.state),
  }

  const applicantBreadcrumbs = useMemo(() => {
    if (!applicationId || !opportunity || !jobId) return undefined
    const applicant = board?.applicants.find((entry) => entry.id === applicationId)
    return [
      { label: 'Job openings', to: jobsListBackTo },
      { label: opportunity.title, to: `/employer/jobs/${jobId}/applicants` },
      { label: applicant?.profile.name ?? 'Applicant' },
    ]
  }, [applicationId, board?.applicants, jobId, jobsListBackTo, opportunity])

  const goToTab = (tab: JobWorkspaceTab) => {
    navigateWithThreadPreservation(navigate, `/employer/jobs/${jobId}/${tab}`, searchParams, {
      state: location.state,
    })
  }

  const patchOpportunityCache = useCallback(
    (updated: OrganisationOpportunity) => {
      queryClient.setQueryData(['opportunity', jobId], updated)
      const organisationSlug = updated.organisation_slug
      if (organisationSlug) {
        queryClient.setQueryData(
          ['organisation-opportunity', organisationSlug, jobId],
          updated,
        )
        void queryClient.invalidateQueries({
          queryKey: ['organisation-opportunities', organisationSlug],
        })
      }
    },
    [jobId, queryClient],
  )

  const handleFixPublishGaps = useCallback(() => {
    if (!opportunity || !jobId) return
    // Open the details editor from the layout. Do not navigate first — a path
    // change would dismiss the canvas (artifact-canvas route sync).
    jobDetailsOpenKeyRef.current += 1
    openCanvas({
      title: 'Edit job details',
      preventAutoFocus: true,
      content: (
        <OpportunityDetailsCanvas
          key={`job-details-${jobDetailsOpenKeyRef.current}`}
          organisationSlug={opportunity.organisation_slug ?? ''}
          opportunityId={jobId}
          opportunity={opportunity}
          onClose={closeCanvas}
          onSaved={patchOpportunityCache}
        />
      ),
    })
  }, [closeCanvas, jobId, openCanvas, opportunity, patchOpportunityCache])

  const isDraftJob = opportunity?.publication_status === 'draft'
  const isInviteAvailable = lifecycleInviteAvailable(opportunity?.publication_status)
  const inviteUnavailableTitle = inviteUnavailableTabTitle(opportunity?.publication_status)

  const jobWorkspaceTabItems = useMemo((): PageLineTabItem[] => {
    const talentItem: PageLineTabItem = isDraftJob
      ? {
          value: 'talent',
          label: 'Talent',
          icon: Users,
          unavailableTitle: PUBLISH_FIRST_TAB_TITLE,
        }
      : {
          value: 'talent',
          label: 'Talent',
          icon: Users,
        }

    const suggestionsItem: PageLineTabItem = !isInviteAvailable
      ? {
          value: 'suggestions',
          label: 'Invite',
          icon: UserPlus,
          unavailableTitle: inviteUnavailableTitle,
        }
      : {
          value: 'suggestions',
          label: 'Invite',
          icon: UserPlus,
          ...(inviteSuggestionsCount > 0
            ? { badge: <TabCountBadge count={inviteSuggestionsCount} /> }
            : {}),
        }

    return [
      { value: 'details', label: 'Details', icon: FileText },
      {
        value: 'applicants',
        label: 'Pipeline',
        icon: Kanban,
        ...(pipelineCount > 0 ? { badge: <TabCountBadge count={pipelineCount} /> } : {}),
      },
      talentItem,
      suggestionsItem,
    ]
  }, [
    inviteSuggestionsCount,
    inviteUnavailableTitle,
    isDraftJob,
    isInviteAvailable,
    pipelineCount,
  ])

  const handleTabChange = (value: string) => {
    const tab = value as JobWorkspaceTab
    if (isDraftJob && tab === 'talent') {
      return
    }
    if (!isInviteAvailable && tab === 'suggestions') {
      return
    }
    goToTab(tab)
  }

  useEffect(() => {
    if (!jobId || isLoading) return
    if (isDraftJob && activeTab === 'talent') {
      goToTab('details')
      return
    }
    if (!isInviteAvailable && activeTab === 'suggestions') {
      goToTab('details')
    }
  }, [activeTab, isLoading, jobId, isDraftJob, isInviteAvailable])

  useEffect(() => {
    if (!jobId || isLoading || !isInviteAvailable) return
    void prefetchJobProspectMatches(queryClient, jobId)
  }, [jobId, isLoading, isInviteAvailable, queryClient])

  const jobDetailUrl = publishedJobPublicUrl(
    opportunity?.publication_status,
    opportunity?.public_slug,
  )

  useEffect(() => {
    if (!jobDetailUrl) return
    if (searchParams.get('linkedin_share') !== 'connected') return
    setLinkedInShareOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('linkedin_share')
    const qs = next.toString()
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, {
      replace: true,
      state: location.state,
    })
  }, [searchParams, jobDetailUrl, location.pathname, location.state, navigate])

  if (isLoading) {
    return <EntityDetailSkeleton variant="job" ariaLabel="Loading job" />
  }

  if (error || !opportunity || !jobId) {
    return (
      <PageContainer>
        <PageHeaderLeading back={jobsListBack} />
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Job not found</h1>
          <p className="mb-6 text-muted-foreground">
            This posting may have been removed or you may not have access.
          </p>
          <Button
            type="button"
            onClick={() =>
              navigateWithThreadPreservation(
                navigate,
                jobsListBackTo,
                searchParams,
                jobsListBack.state !== undefined ? { state: jobsListBack.state } : undefined,
              )
            }
          >
            Back to job openings
          </Button>
        </div>
      </PageContainer>
    )
  }

  const orgName = opportunity.organisation_name ?? opportunity.organisation_slug ?? ''

  return (
    <PageContainer
      padding={jobWorkspacePagePadding}
      maxWidth="full"
      contained={false}
      className={cn('flex flex-col', pagePaneFill && 'h-full overflow-hidden')}
    >
      <div className="shrink-0">
        <PageHeaderStack compact>
        <PageHeader
          className="gap-3"
          leading={
            <PageHeaderLeading
              breadcrumbs={applicantBreadcrumbs}
              back={
                applicantBreadcrumbs
                  ? undefined
                  : jobsListBack
              }
            />
          }
          title={opportunity.title}
          actions={
            <JobWorkspaceHeaderActions
              opportunity={opportunity}
              token={token}
              queryClient={queryClient}
              jobDetailUrl={jobDetailUrl}
              onShareLinkedIn={
                jobDetailUrl && opportunity.publication_status === 'published'
                  ? () => setLinkedInShareOpen(true)
                  : undefined
              }
              onAfterTransition={(action) => {
                if (action === 'publish') {
                  goToTab('applicants')
                }
              }}
              onFixPublishGaps={handleFixPublishGaps}
              onDeleted={() => {
                navigateWithThreadPreservation(navigate, '/employer/jobs', searchParams)
              }}
              onDuplicated={(newId) => {
                navigateWithThreadPreservation(
                  navigate,
                  `/employer/jobs/${newId}/details`,
                  searchParams,
                )
              }}
            />
          }
        />

        <PageLineTabs
          value={activeTab}
          onValueChange={handleTabChange}
          ariaLabel="Job workspace sections"
          items={jobWorkspaceTabItems}
        />
        </PageHeaderStack>
      </div>

      <div
        className={cn(
          pagePaneFill
            ? 'min-h-0 flex flex-1 flex-col overflow-hidden'
            : 'pb-6',
        )}
      >
        <Outlet
          context={{
            opportunity,
            opportunityId: jobId,
            orgName,
          }}
        />
      </div>

      {jobDetailUrl ? (
        <LinkedInShareJobDialog
          open={linkedInShareOpen}
          onOpenChange={setLinkedInShareOpen}
          opportunity={opportunity}
          shareUrl={jobDetailUrl}
          token={token}
        />
      ) : null}
    </PageContainer>
  )
}

export function JobWorkspaceSetupRoute() {
  const { jobId = '' } = useParams<{ jobId: string }>()
  const { data: opportunity, isLoading } = useOpportunity(jobId)

  if (isLoading || !opportunity) {
    return <EntityDetailSkeleton variant="job" ariaLabel="Loading job details" />
  }

  const orgName = opportunity.organisation_name ?? opportunity.organisation_slug ?? ''

  return (
    <JobWorkspaceSetupTab
      opportunity={opportunity}
      opportunityId={jobId}
      orgName={orgName}
    />
  )
}

export function JobWorkspaceApplicantsRoute() {
  return <JobWorkspaceApplicantsTab />
}

export function JobWorkspaceTalentRoute() {
  return <JobWorkspaceTalentTab />
}

/** @deprecated Use JobWorkspaceTalentRoute */
export function JobWorkspaceAnalysisRoute() {
  return <JobWorkspaceTalentTab />
}

export function JobWorkspaceSuggestionsRoute() {
  return <JobWorkspaceSuggestionsTab />
}
